import { ApplyTo } from '../ui/components/PromotionForm/Restrictions/typings'
import type { Budget } from './budget'
import type { Item } from './components'
import { BIN, SubscriptionFrequencyUnit } from './conditions'

export enum PROMOTION_TYPE {
  REGULAR = 'regular',
  BUY_TOGETHER = 'combo',
  MORE_FOR_LESS = 'forThePriceOf',
  PROGRESSIVE = 'progressive',
  BUY_AND_WIN = 'buyAndWin',
  CAMPAIGN = 'campaign',
}

export type CalculatorType =
  | PROMOTION_TYPE.REGULAR
  | PROMOTION_TYPE.BUY_TOGETHER
  | PROMOTION_TYPE.MORE_FOR_LESS
  | PROMOTION_TYPE.PROGRESSIVE
  | PROMOTION_TYPE.BUY_AND_WIN
  | PROMOTION_TYPE.CAMPAIGN
  | 'maxPricePerItem'
  | 'tax'
  | 'multipleEffects'

export type CalculatorTypePayload =
  | PROMOTION_TYPE.REGULAR
  | 'Regular'
  | PROMOTION_TYPE.BUY_TOGETHER
  | 'Combo'
  | PROMOTION_TYPE.MORE_FOR_LESS
  | 'ForThePriceOf'
  | PROMOTION_TYPE.PROGRESSIVE
  | 'Progressive'
  | PROMOTION_TYPE.BUY_AND_WIN
  | 'BuyAndWin'
  | PROMOTION_TYPE.CAMPAIGN
  | 'Campaign'
  | 'maxPricePerItem'
  | 'MaxPricePerItem'
  | 'tax'
  | 'Tax'
  | 'multipleEffects'
  | 'MultipleEffects'

export enum DiscountType {
  Price = 'price',
  Shipping = 'shipping',
  Gift = 'gift',
  Formula = 'formula',
  Loyalty = 'loyalty',
}

export type AdditionalInformation = {
  name: string
  value: string
}

export type Weekday =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export type TimeInterval = { start?: Date; end?: Date }

export interface SkusGift {
  quantitySelectable: number
  gifts?: {
    id?: string
    name?: string
    quantity?: number
    sellers?: Item[]
  }[]
}

export const MARKETPLACE = 'Marketplace' as const
export const FULFILLMENT = 'Fulfillment' as const

export type Origin = typeof MARKETPLACE | typeof FULFILLMENT

export const enum OPERATORS {
  ALL = 'all',
  EQUALS = 'equals',
  DIFFERENT = 'different',
}

export type ListOperator = OPERATORS.EQUALS | OPERATORS.DIFFERENT

export interface ItemRestriction {
  items: Item[]
  operator: ListOperator
}

export enum SellerOperator {
  ALL = OPERATORS.ALL,
  EQUALS = OPERATORS.EQUALS,
  DIFFERENT = OPERATORS.DIFFERENT,
  OPTIN = 'optIn',
}
export interface SellerRestriction {
  items: Item[]
  operator: SellerOperator
}

export enum SCOPE_RESTRICTION_TYPE {
  CATEGORIES = 'categories',
  BRANDS = 'brands',
  PRODUCTS = 'products',
  SKUS = 'skus',
}

export interface Scope {
  [SCOPE_RESTRICTION_TYPE.CATEGORIES]: ItemRestriction
  [SCOPE_RESTRICTION_TYPE.BRANDS]: ItemRestriction
  [SCOPE_RESTRICTION_TYPE.PRODUCTS]: ItemRestriction
  [SCOPE_RESTRICTION_TYPE.SKUS]: ItemRestriction
}

export type ItemsToConsider =
  | 'AllItems'
  | 'IncludeMatchedItems'
  | 'ExcludeMatchedItems'

export type ItemPriceRange = {
  min: number | undefined
  max: number | undefined
}

export enum ZIP_CODE_RANGE_OPERATOR {
  INCLUDE = 'include',
  EXCLUDE = 'exclude',
}

export type ZipCodeRange = {
  from: string
  to: string
  operator: ZIP_CODE_RANGE_OPERATOR
}

export interface Scheduling {
  start: Date
  end: Date | undefined
}

export interface Recurrence {
  weekdays: Weekday[]
  timeIntervals: TimeInterval[]
}

export type RnbCalculatorPayload = {
  idCalculatorConfiguration: string
  name: string
  description: string
  generalValues?: Record<string, string>
  beginDateUtc: string
  endDateUtc?: string
  lastModified?: string
  isActive: boolean
  isArchived: boolean
  isFeatured: boolean
  disableDeal: boolean
  activeDaysOfWeek: string[]
  offset?: number // deprecated: use newOffset instead
  newOffset: number
  recurrencyCrons?: string[]

  // Discount section
  nominalShippingDiscountValue: number
  absoluteShippingDiscountValue: number
  nominalDiscountValue: number
  nominalDiscountType: 'cart' | 'item'
  maximumUnitPriceDiscount: number
  percentualDiscountValue: number
  rebatePercentualDiscountValue: number
  percentualShippingDiscountValue: number
  percentualTax: number
  shippingPercentualTax: number
  taxExpression: string
  discountExpression: string
  percentualDiscountValueList1: number
  percentualDiscountValueList2: number
  skusGift: SkusGift
  activateGiftsMultiplier: boolean
  nominalRewardValue: number
  percentualRewardValue: number
  orderStatusRewardValue: 'payment-approved' | 'invoiced'
  nominalTax: number
  priceTableName?: string

  // Scope section
  categories: Item[]
  categoriesAreInclusive: boolean
  brands: Item[]
  brandsAreInclusive: boolean
  products: Item[]
  productsAreInclusive: boolean
  skus: Item[]
  skusAreInclusive: boolean
  collections1BuyTogether: Item[]
  collections2BuyTogether: Item[]
  minimumQuantityBuyTogether: number
  quantityToAffectBuyTogether: number
  enableBuyTogetherPerSku: boolean
  listSku1BuyTogether: Item[]
  listSku2BuyTogether: Item[]
  listCategory1BuyTogether: Item[]

  // Restrictions section
  applyToAllShippings: boolean
  maxNumberOfAffectedItems: number
  maxNumberOfAffectedItemsGroupKey: 'perProductId' | 'perCart' | 'perSku'
  origin: Origin
  idSeller?: string | null
  idSellerIsInclusive: boolean | null
  idsSalesChannel: string[]
  areSalesChannelIdsExclusive: boolean
  marketingTags?: string[]
  marketingTagsAreNotInclusive?: boolean
  paymentsMethods: Item[]
  stores: Item[]
  campaigns: Item[]
  storesAreInclusive: boolean
  collections: Item[]
  collectionsIsInclusive: boolean

  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmiCampaign?: string
  coupon: string[]
  totalValueFloor: number
  totalValueCeling: number
  totalValueIncludeAllItems: boolean
  totalValueMode: ItemsToConsider
  restrictionsBins: BIN
  cardIssuers: string[]
  totalValuePurchase: number
  slasIds: string[]
  isSlaSelected: boolean
  isFirstBuy: boolean
  firstBuyIsProfileOptimistic: boolean
  compareListPriceAndPrice: boolean
  isDifferentListPriceAndPrice: boolean
  zipCodeRanges?: Array<{
    zipCodeFrom: string
    zipCodeTo: string
    inclusive: boolean
  }>
  countries?: string[]
  itemMaxPrice: number
  itemMinPrice: number
  installment: number
  isMinMaxInstallments: boolean
  minInstallment: number
  maxInstallment: number
  merchants: string[]
  clusterExpressions: string[]
  piiClusterExpressions: string[]
  clusterOperator: 'All' | 'Any'
  paymentsRules: Item[]
  giftListTypes: Item[]
  productsSpecifications: Array<{
    fieldId: string
    fieldName: string
    isFieldsValuesInclusive: boolean
    fieldValues: { id?: number; name: string; value: string }
  }>
  affiliates: Item[]
  affiliatesIsInclusive?: boolean
  maxUsage: number
  maxUsagePerClient: number
  shouldDistributeDiscountAmongMatchedItems: boolean
  accumulateWithManualPrice: boolean | null
  type: CalculatorTypePayload
  subscriptions?: {
    plan?: {
      frequency: {
        periodicity: SubscriptionFrequencyUnit
        interval: number
      }
    }
    execution: {
      firstOrders: boolean
      recurrentOrders?: {
        minCount?: number
        maxCount?: number
      }
    }
  }
  useNewProgressiveAlgorithm: boolean
  percentualDiscountValueList: Array<{
    quantity: number
    percent: number
  }>
  idTypeDiscountBuyTogether: string // compatibility. ignore
  isAppliedToMostExpensive?: boolean
  cumulative: boolean
  optIn?: {
    sellers: string[]
  } | null
  budget?: {
    budgetId: string
    allocationId: string
  }
}

export enum Attributes {
  CART = 'cart',
  PRODUCT = 'product',
  SKU = 'sku',
}

export type MaxItemsPerAttribute = {
  attribute: Attributes
  value: number
  applyTo: ApplyTo
}

export type MaxItemPerCart = { value: number; applyTo?: ApplyTo }

export interface Restrictions {
  maxUsage: number | undefined
  maxUsagePerClient: number | undefined
  maxItemsPerCart: MaxItemPerCart | undefined
  maxItemsPerAttribute: MaxItemsPerAttribute | undefined
  accumulate: boolean
  accumulateWithManualPrices: boolean
  sellers: SellerRestriction | undefined
  salesChannels: ItemRestriction | undefined
  origin: Origin
  budget: Budget | undefined
}

export type Storefront = {
  highlight: boolean
  additionalInformation: AdditionalInformation[]
}

export type AdvancedScheduling = Scheduling & {
  recurrence: Recurrence | undefined
}
